import * as CL from '@design-system/component-library';
import { Grid } from '../Grid/Grid';
import { JsonLd } from 'react-schemaorg';
import { MobileCarouselWrapper, getItemList, onSalesProductOrder } from './SubscriptionUtils';
import { SiteContext } from '../../public/site/SiteContext';
import { SubscriptionCard } from './SubscriptionCard';
import {
  downloadSpeed,
  kBitsPerSecondMsg,
  mBpsMsg,
  mobileM2MMsg,
  orderMsg,
  t,
  uploadSpeedMsg,
} from '../../common/i18n';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import type { ItemList } from 'schema-dts';
import type { MobileBroadbandSubCardProps } from '../SubscriptionCard/SubscriptionCard';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { SubscriptionCardWithModel } from './types';

export interface HorizontalSubscriptionsProps {
  subscriptionCards: MobileBroadbandSubCardProps[];
  publicPages: boolean;
  listName: string;
  isChangeOffer?: boolean;
  onlineModels?: OnlineModel[];
}

export const HorizontalM2MSubscriptions = ({
  subscriptionCards,
  publicPages,
  listName,
  isChangeOffer = false,
}: HorizontalSubscriptionsProps) => {
  const { pathname } = useLocation();
  const { siteBaseUrl } = useContext(SiteContext);

  let analyticsIndexLaitenetti4G = 0;
  let analyticsIndexLaitenetti5G = 0;

  const getSubscription = (cardName: string): SubscriptionCardWithModel | undefined =>
    (subscriptionCards as SubscriptionCardWithModel[])?.find(card => card.cardName === cardName) ?? undefined;

  const sharedProps = {
    publicPages,
    isChangeOffer,
    listName,
    buttonMakesOrder: true,
    onOrder: onSalesProductOrder,
    hasQuantity: true,
  };

  const LaitenettiS = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('S')}
      {...sharedProps}
      onOrder={onSalesProductOrder}
      analyticsIndex={analyticsIndexLaitenetti4G++}
      badges={[]}
      subHeader={t.N7KF(mobileM2MMsg)}
      header="Laitenetti S"
      description={t.A7CC('Sending photos, SMS/voice-controlled devices, property alarms, air source heat pumps')}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.B69Q(kBitsPerSecondMsg, '256')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.B69Q(kBitsPerSecondMsg, '256')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '5,5'),
        },
      ]}
      hasQuantity
      buttonText={t.AQOL(orderMsg)}
    />
  );

  const LaitenettiM = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('M')}
      {...sharedProps}
      analyticsIndex={analyticsIndexLaitenetti4G++}
      badges={[<CL.Badge key="suositus" color="blue-light" text={t.TJPP('Recommended')} />]}
      subHeader={t.N7KF(mobileM2MMsg)}
      header="Laitenetti M"
      description={t.A7CD(
        'For surveillance and trail cameras (photos and videos), for alarm devices, for GPS trackers, for condition monitoring sensors'
      )}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.B69Q(kBitsPerSecondMsg, '256')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '5')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '7,8'),
        },
      ]}
      buttonText={t.AQOL(orderMsg)}
    />
  );

  const LaitenettiL = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('L')}
      {...sharedProps}
      analyticsIndex={analyticsIndexLaitenetti4G++}
      badges={[]}
      subHeader={t.N7KF(mobileM2MMsg)}
      header="Laitenetti L"
      description={t.A7CE(
        'For surveillance and trail cameras (live video), for mobile routers, for network devices, for alarm devices'
      )}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.B69Q(kBitsPerSecondMsg, '512')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '10')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '11'),
        },
      ]}
      buttonText={t.AQOL(orderMsg)}
    />
  );

  const Laitenetti5G = () => (
    <SubscriptionCard
      subscriptionCard={getSubscription('5G')}
      {...sharedProps}
      analyticsIndex={analyticsIndexLaitenetti5G++}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      subHeader={t.N7KF(mobileM2MMsg)}
      header="Laitenetti 5G"
      description={t.JQD9(
        'For uploading high quality live video. Allows you to benefit from new technological innovations and devices, as well as IOT applications and software.'
      )}
      infoItems={[
        {
          icon: 'download',
          text: `${t.R4O4(downloadSpeed)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '100')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '28'),
        },
      ]}
      buttonText={t.AQOL(orderMsg)}
    />
  );

  return (
    <Grid colWidthL={publicPages ? 10 : 12}>
      <div className="of-subscriptions-main-container">
        <h2 className="of-subscriptions-main-container-carousel-title">
          {t.OD42('5G Laitenetti package for companies')}
        </h2>
        <MobileCarouselWrapper>
          <Laitenetti5G />
        </MobileCarouselWrapper>
        <h2 className="of-subscriptions-main-container-carousel-title">
          {t.OD41('4G Laitenetti packages for companies')}
        </h2>
        <MobileCarouselWrapper>
          <LaitenettiS />
          <LaitenettiM />
          <LaitenettiL />
        </MobileCarouselWrapper>
        <JsonLd<ItemList> item={getItemList(subscriptionCards as SubscriptionCardWithModel[], siteBaseUrl, pathname)} />
      </div>
    </Grid>
  );
};
